import { useApiSearch } from './useApiSearch';
import { useServerList } from './useServerList';
import type { ListParams } from '@/api/pagedList';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { queryKeys } from '@/lib/queryKeys';
import * as api from '@/api/academics';
import type {
  AcademicClassInput,
  AcademicSectionInput,
  AcademicSubjectInput,
  AcademicYearInput,
  AcademicYearRolloverInput,
  ClassSectionInput,
  ClassSectionSubjectInput,
  HouseInput,
  RolloverFinishInput,
} from '@/models';
import { useCurrentSchool } from './useCurrentSchool';
import { useMutationWithToast } from './useMutationWithToast';
import { useRequireSchoolId, useSchoolScopedQuery } from './useSchoolScopedQuery';

// Years, classes, sections and subjects belong to the school chosen in the
// header (useCurrentSchool); every hook here works in that school.
//
// Every change here can change what a dropdown offers, so the mutations also
// invalidate the lookup cache (queryKeys.lookups.all).

// --- Academic years -----------------------------------------------------
export const useAcademicYears = (schoolId?: number) =>
  useSchoolScopedQuery(queryKeys.academics.yearsFor, api.listYears, schoolId, { staleTime: 5 * 60_000 });

/** Creates a year, or updates it when `yearId` is given. */
export const useSaveAcademicYear = () => {
  const schoolId = useRequireSchoolId();
  return useMutationWithToast({
    mutationFn: (vars: { yearId?: number; input: AcademicYearInput }) =>
      api.saveYear(schoolId(), vars.yearId, vars.input),
    invalidate: [queryKeys.academics.years, queryKeys.lookups.all],
    successMessage: 'Academic year saved.',
  });
};

/** Makes a year the school's current year (clears the flag on the others). */
export const useSetCurrentAcademicYear = () => {
  const schoolId = useRequireSchoolId();
  return useMutationWithToast({
    mutationFn: (yearId: number) => api.setCurrentYear(schoolId(), yearId),
    invalidate: [queryKeys.academics.years, queryKeys.lookups.all],
    successMessage: (year) => `${year.yearName} is now the current academic year.`,
  });
};

export const useDeleteAcademicYear = () => {
  const schoolId = useRequireSchoolId();
  return useMutationWithToast({
    mutationFn: (yearId: number) => api.deleteYear(schoolId(), yearId),
    invalidate: [queryKeys.academics.years, queryKeys.lookups.all],
    successMessage: 'Academic year deleted.',
  });
};

// --- Year rollover ("Start new year") -------------------------------------
// The copy changes most year-scoped data of the target year, so a run (and a carry)
// invalidates the academics, enrollment and fee caches along with the preview.

/** What a rollover from `fromYearId` into `toYearId` would copy, and whether the caller may run it. */
export const useRolloverPreview = (
  toYearId: number | null,
  fromYearId: number | null,
  steps?: readonly string[] | null,
) => {
  const { currentSchoolId } = useCurrentSchool();
  return useQuery({
    queryKey: [...queryKeys.academics.rolloverPreview(currentSchoolId, toYearId, fromYearId), steps ?? null],
    queryFn: () =>
      api.getRolloverPreview(currentSchoolId as number, toYearId as number, fromYearId as number, steps),
    // Ticking a step re-asks with the new choice; keep the table on screen meanwhile.
    placeholderData: keepPreviousData,
    enabled: currentSchoolId !== null && toYearId !== null && fromYearId !== null && toYearId !== fromYearId,
  });
};

/** Copies the chosen setup steps into the target year; a rerun skips what is already there. */
export const useRunRollover = () => {
  const schoolId = useRequireSchoolId();
  return useMutationWithToast({
    mutationFn: (vars: { toYearId: number; input: AcademicYearRolloverInput }) =>
      api.runRollover(schoolId(), vars.toYearId, vars.input),
    invalidate: [
      queryKeys.academics.rolloverPreviews,
      queryKeys.academics.classSections,
      queryKeys.academics.classSectionSubjects,
      queryKeys.fees.terms,
      queryKeys.fees.structures,
      queryKeys.lookups.all,
    ],
    successMessage: (result) => {
      const created = result.steps.reduce((sum, s) => sum + s.created, 0);
      const skipped = result.steps.reduce((sum, s) => sum + s.skipped, 0);
      return `Setup copied: ${created} created, ${skipped} already there.`;
    },
  });
};

/** Carries the source year's unpaid fees into the target year as one arrears bill per student. */
export const useCarryArrears = () => {
  const schoolId = useRequireSchoolId();
  return useMutationWithToast({
    mutationFn: (vars: { toYearId: number; fromYearId: number }) =>
      api.carryArrears(schoolId(), vars.toYearId, vars.fromYearId),
    invalidate: [queryKeys.academics.rolloverPreviews, ['fees']],
    successMessage: (result) =>
      result.created > 0
        ? `Arrears carried for ${result.created} ${result.created === 1 ? 'student' : 'students'}.`
        : 'No new arrears to carry.',
  });
};

/** Makes the target year current (closing the source year when asked). */
export const useFinishRollover = () => {
  const schoolId = useRequireSchoolId();
  return useMutationWithToast({
    mutationFn: (vars: { toYearId: number; input: RolloverFinishInput }) =>
      api.finishRollover(schoolId(), vars.toYearId, vars.input),
    invalidate: [queryKeys.academics.years, queryKeys.academics.rolloverPreviews, queryKeys.lookups.all],
    successMessage: (year) => `${year.yearName} is now the current academic year.`,
  });
};

// --- Classes ---------------------------------------------------------
export const useClasses = (schoolId?: number) =>
  useSchoolScopedQuery(queryKeys.academics.classesFor, api.listClasses, schoolId);

/** Creates a class, or updates it when `classId` is given. */
export const useSaveClass = () => {
  const schoolId = useRequireSchoolId();
  return useMutationWithToast({
    mutationFn: (vars: { classId?: number; input: AcademicClassInput }) =>
      api.saveClass(schoolId(), vars.classId, vars.input),
    invalidate: [queryKeys.academics.classes, queryKeys.academics.classSections, queryKeys.lookups.all],
    successMessage: 'Class saved.',
  });
};

export const useDeleteClass = () => {
  const schoolId = useRequireSchoolId();
  return useMutationWithToast({
    mutationFn: (classId: number) => api.deleteClass(schoolId(), classId),
    invalidate: [queryKeys.academics.classes, queryKeys.academics.classSections, queryKeys.lookups.all],
    successMessage: 'Class deleted.',
  });
};

// --- Sections -------------------------------------------------------
export const useSections = () => useSchoolScopedQuery(queryKeys.academics.sectionsFor, api.listSections);

/** Creates a section, or updates it when `sectionId` is given. */
export const useSaveSection = () => {
  const schoolId = useRequireSchoolId();
  return useMutationWithToast({
    mutationFn: (vars: { sectionId?: number; input: AcademicSectionInput }) =>
      api.saveSection(schoolId(), vars.sectionId, vars.input),
    invalidate: [queryKeys.academics.sections, queryKeys.academics.classSections, queryKeys.lookups.all],
    successMessage: 'Section saved.',
  });
};

export const useDeleteSection = () => {
  const schoolId = useRequireSchoolId();
  return useMutationWithToast({
    mutationFn: (sectionId: number) => api.deleteSection(schoolId(), sectionId),
    invalidate: [queryKeys.academics.sections, queryKeys.academics.classSections, queryKeys.lookups.all],
    successMessage: 'Section deleted.',
  });
};

// --- Subjects ------------------------------------------------------
export const useSubjects = (enabled = true) =>
  useSchoolScopedQuery(queryKeys.academics.subjectsFor, api.listSubjects, undefined, { enabled });

/** Creates a subject, or updates it when `subjectId` is given. */
export const useSaveSubject = () => {
  const schoolId = useRequireSchoolId();
  return useMutationWithToast({
    mutationFn: (vars: { subjectId?: number; input: AcademicSubjectInput }) =>
      api.saveSubject(schoolId(), vars.subjectId, vars.input),
    invalidate: [queryKeys.academics.subjects, queryKeys.academics.classSectionSubjects, queryKeys.lookups.all],
    successMessage: 'Subject saved.',
  });
};

export const useDeleteSubject = () => {
  const schoolId = useRequireSchoolId();
  return useMutationWithToast({
    mutationFn: (subjectId: number) => api.deleteSubject(schoolId(), subjectId),
    invalidate: [queryKeys.academics.subjects, queryKeys.lookups.all],
    successMessage: 'Subject deleted.',
  });
};

// --- Houses --------------------------------------------------------
export const useHouses = () => useSchoolScopedQuery(queryKeys.academics.housesFor, api.listHouses);

/** Creates a house, or updates it when `houseId` is given. */
export const useSaveHouse = () => {
  const schoolId = useRequireSchoolId();
  return useMutationWithToast({
    mutationFn: (vars: { houseId?: number; input: HouseInput }) => api.saveHouse(schoolId(), vars.houseId, vars.input),
    invalidate: [queryKeys.academics.houses],
    successMessage: 'House saved.',
  });
};

export const useDeleteHouse = () => {
  const schoolId = useRequireSchoolId();
  return useMutationWithToast({
    mutationFn: (houseId: number) => api.deleteHouse(schoolId(), houseId),
    invalidate: [queryKeys.academics.houses],
    successMessage: 'House deleted.',
  });
};

// --- Class sections ------------------------------------------------
/** Defaults to the current school, as before; pass `schoolId` to scope it explicitly. */
export const useClassSections = (schoolId?: number) =>
  useSchoolScopedQuery(queryKeys.academics.classSectionsFor, api.listClassSections, schoolId);

/** Links a section to a class, or updates the link when `classSectionId` is given. */
export const useSaveClassSection = () =>
  useMutationWithToast({
    mutationFn: (vars: { classId: number; classSectionId?: number; input: ClassSectionInput }) =>
      api.saveClassSection(vars.classId, vars.classSectionId, vars.input),
    invalidate: [queryKeys.academics.classSections, queryKeys.academics.classSectionSubjects, queryKeys.lookups.all],
    successMessage: (_, vars) => (vars.classSectionId ? 'Class section updated.' : 'Section linked to class.'),
  });

export const useDeleteClassSection = () =>
  useMutationWithToast({
    mutationFn: (vars: { classId: number; classSectionId: number }) =>
      api.deleteClassSection(vars.classId, vars.classSectionId),
    invalidate: [queryKeys.academics.classSections, queryKeys.lookups.all],
    successMessage: 'Section unlinked from class.',
  });

// --- Class section subjects -----------------------------------------
export const useClassSectionSubjects = () =>
  useSchoolScopedQuery(queryKeys.academics.classSectionSubjectsFor, api.listClassSectionSubjects);

/** Assigns a subject to a class section, or updates it when `classSectionSubjectId` is given. */
export const useSaveClassSectionSubject = () =>
  useMutationWithToast({
    mutationFn: (vars: {
      classSectionId: number;
      classSectionSubjectId?: number;
      input: ClassSectionSubjectInput;
    }) => api.saveClassSectionSubject(vars.classSectionId, vars.classSectionSubjectId, vars.input),
    invalidate: [queryKeys.academics.classSectionSubjects, queryKeys.lookups.all],
    successMessage: (_, vars) => (vars.classSectionSubjectId ? 'Subject teacher updated.' : 'Subject assigned.'),
  });

export const useDeleteClassSectionSubject = () =>
  useMutationWithToast({
    mutationFn: (vars: { classSectionId: number; classSectionSubjectId: number }) =>
      api.deleteClassSectionSubject(vars.classSectionId, vars.classSectionSubjectId),
    invalidate: [queryKeys.academics.classSectionSubjects, queryKeys.lookups.all],
    successMessage: 'Subject unassigned.',
  });

export const useAcademicYearsTable = (params: ListParams = {}, schoolId?: number) => useServerList(api.listYearsPage, queryKeys.academics.years, params, { aliases: { yearId: 'academicYearId', yearStartDate: 'startDate', yearEndDate: 'endDate', }, schoolId });
export const useClassesTable = (params: ListParams = {}, schoolId?: number) => useServerList(api.listClassesPage, queryKeys.academics.classes, params, { schoolId });
export const useSectionsTable = (params: ListParams = {}, schoolId?: number) => useServerList(api.listSectionsPage, queryKeys.academics.sections, params, { schoolId });
export const useHousesTable = (params: ListParams = {}, schoolId?: number) => useServerList(api.listHousesPage, queryKeys.academics.houses, params, { schoolId, aliases: { status: 'isActive' } });

export const useClassesSearch = (search: string, params: Record<string, unknown> = {}, skipEmpty = false) => useApiSearch(api.searchClasses, queryKeys.academics.classes, search, params, true, false, skipEmpty);

export const useClassSectionSubjectsSearch = (search: string, params: Record<string, unknown> = {}) => useApiSearch(api.searchClassSectionSubjects, queryKeys.academics.classSectionSubjects, search, params);

export const useClassSectionsSearch = (search: string) => useApiSearch(api.searchClassSections, queryKeys.academics.classSections, search);
